import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Sparkles, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STRINGS = {
  tr: {
    title: 'Menü Asistanı',
    subtitle: 'Bugün ne yemek istersiniz?',
    placeholder: 'Bir şey sorun... (örn: vejetaryen ne var?)',
    welcome: 'Merhaba! Ben Central\'ın AI menü asistanıyım. Size en uygun yemekleri önerebilir, malzemeler hakkında bilgi verebilirim.',
    send: 'Gönder',
    error: 'Bir hata oluştu, tekrar deneyin.',
  },
  en: {
    title: 'Menu Assistant',
    subtitle: 'What would you like today?',
    placeholder: 'Ask anything... (e.g. what is vegetarian?)',
    welcome: 'Hi! I\'m Central\'s AI menu assistant. I can recommend dishes and answer questions about ingredients.',
    send: 'Send',
    error: 'Something went wrong, please try again.',
  },
  me: {
    title: 'Asistent Menija',
    subtitle: 'Šta biste danas?',
    placeholder: 'Pitajte bilo šta... (npr. vegetarijanske opcije?)',
    welcome: 'Zdravo! Ja sam AI asistent menija Central-a. Mogu preporučiti jela i odgovoriti na pitanja o sastojcima.',
    send: 'Pošalji',
    error: 'Došlo je do greške, pokušajte ponovo.',
  },
  ru: {
    title: 'Ассистент Меню',
    subtitle: 'Что бы вы хотели сегодня?',
    placeholder: 'Спросите что-нибудь... (напр. что вегетарианское?)',
    welcome: 'Привет! Я AI ассистент меню Central. Могу рекомендовать блюда и ответить на вопросы об ингредиентах.',
    send: 'Отправить',
    error: 'Что-то пошло не так, попробуйте снова.',
  },
  sr: {
    title: 'Асистент Менија',
    subtitle: 'Шта бисте данас?',
    placeholder: 'Питајте било шта... (нпр. вегетаријанске опције?)',
    welcome: 'Здраво! Ја сам AI асистент менија Central-а. Могу препоручити јела и одговорити на питања о састојцима.',
    send: 'Пошаљи',
    error: 'Дошло је до грешке, покушајте поново.',
  },
};

const MenuChatbot = () => {
  const { language } = useLanguage();
  const t = STRINGS[language] || STRINGS.tr;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: 'assistant', text: t.welcome }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  useEffect(() => {
    // Refresh welcome on language change (only if conversation is empty besides welcome)
    if (messages.length === 1 && messages[0].role === 'assistant') {
      setMessages([{ role: 'assistant', text: t.welcome }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || loading) return;
    setMessages(prev => [...prev, { role: 'user', text: msg }]);
    setInput('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/ai/menu-chat`, {
        message: msg,
        session_id: sessionRef.current,
        language,
      });
      sessionRef.current = res.data.session_id;
      setMessages(prev => [...prev, { role: 'assistant', text: res.data.reply }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', text: t.error, isError: true }]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating button */}
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.5, type: 'spring' }}
        onClick={() => setOpen(true)}
        className="fixed bottom-24 right-4 md:bottom-8 md:right-8 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-terracotta to-terracotta/80 text-white shadow-2xl shadow-terracotta/40 flex items-center justify-center hover:scale-110 active:scale-95 transition-transform"
        data-testid="ai-chatbot-fab"
        aria-label={t.title}
      >
        <Sparkles className="w-6 h-6" />
        <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-bone animate-pulse" />
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
            onClick={() => setOpen(false)}
          >
            <motion.div
              initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 240 }}
              className="w-full md:w-[28rem] h-[80vh] md:h-[36rem] bg-bone rounded-t-3xl md:rounded-3xl shadow-2xl flex flex-col overflow-hidden ring-1 ring-charcoal/10"
              onClick={(e) => e.stopPropagation()}
              data-testid="ai-chatbot-panel"
            >
              {/* Header */}
              <div className="bg-charcoal text-white p-5 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-terracotta to-terracotta/80 flex items-center justify-center">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="font-playfair text-lg leading-tight">{t.title}</h3>
                  <p className="text-white/60 text-xs">{t.subtitle}</p>
                </div>
                <button onClick={() => setOpen(false)} className="text-white/60 hover:text-white" data-testid="ai-chatbot-close">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Messages */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ai-chatbot-messages">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[82%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === 'user'
                        ? 'bg-terracotta text-white rounded-br-md'
                        : m.isError
                          ? 'bg-rose-100 text-rose-800 rounded-bl-md'
                          : 'bg-white text-charcoal rounded-bl-md ring-1 ring-charcoal/5 shadow-sm'
                    }`}>
                      {m.text}
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 ring-1 ring-charcoal/5 shadow-sm flex items-center gap-2 text-charcoal/60">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">...</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Input */}
              <div className="border-t border-charcoal/10 bg-bone p-3 flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKey}
                  placeholder={t.placeholder}
                  className="flex-1 h-11 px-4 rounded-full bg-white ring-1 ring-charcoal/10 focus:ring-terracotta focus:outline-none text-sm"
                  disabled={loading}
                  data-testid="ai-chatbot-input"
                />
                <button
                  onClick={send}
                  disabled={loading || !input.trim()}
                  className="h-11 w-11 rounded-full bg-terracotta text-white flex items-center justify-center disabled:opacity-50 hover:bg-terracotta/90 active:scale-95 transition-all"
                  data-testid="ai-chatbot-send"
                  aria-label={t.send}
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default MenuChatbot;
